import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Monitor, Server, AlertTriangle, CheckCircle, XCircle, Users, MousePointerClick, CalendarX, DollarSign } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
export function CSRvsSSRSlide() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  const [ssrWidth, setSsrWidth] = useState(0);
  const [csrWidth, setCsrWidth] = useState(0);
  const [ssrLoads, setSsrLoads] = useState(0);
  const [ssrDone, setSsrDone] = useState(false);
  const [csrDone, setCsrDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valRef = useRef(0);
  const ssrCountRef = useRef(0);
  const pausedRef = useRef(false);
  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const startAnimation = useCallback(() => {
    cleanup();
    valRef.current = 0;
    ssrCountRef.current = 0;
    pausedRef.current = false;
    setSsrWidth(0);
    setCsrWidth(0);
    setSsrLoads(0);
    setSsrDone(false);
    setCsrDone(false);
    intervalRef.current = setInterval(() => {
      if (pausedRef.current) return;
      valRef.current += 1;
      const val = valRef.current;
      // SSR cycles 3x within 0-100
      const ssrProg = val * 3 % 100;
      setSsrWidth(Math.min(ssrProg, 100));
      setSsrDone(ssrProg >= 95);
      // Check SSR completions
      if (val === 33 || val === 66 || val === 100) {
        ssrCountRef.current += 1;
        setSsrLoads(ssrCountRef.current);
      }
      // CSR fills linearly
      setCsrWidth(val);
      setCsrDone(val >= 95);
      // When CSR reaches 100, pause — SSR bar stays filled (blue)
      if (val >= 100) {
        pausedRef.current = true;
        // Keep SSR bar at 100% during pause
        setSsrWidth(100);
        setSsrDone(true);
        timeoutRef.current = setTimeout(() => {
          // Reset everything for next loop
          valRef.current = 0;
          ssrCountRef.current = 0;
          pausedRef.current = false;
          setSsrWidth(0);
          setCsrWidth(0);
          setSsrLoads(0);
          setSsrDone(false);
          setCsrDone(false);
        }, 1500);
      }
    }, 30);
  }, [cleanup]);
  useEffect(() => {
    if (isVisible) {
      startAnimation();
    }
    return cleanup;
  }, [isVisible, startAnimation, cleanup]);
  // More realistic funnel based on industry data:
  // Google: 53% of mobile users leave if page takes >3s (Think with Google)
  // Average landing page conversion: 2.35% (Unbounce), top 25% get 5.31%
  // Lead-to-appointment rate: 20-30% for service businesses
  const funnelSteps = [{
    label: '1,000 Monthly Visitors',
    value: '1,000',
    icon: Users,
    loss: null
  }, {
    label: 'After 3-5s Load Time',
    value: '530',
    icon: XCircle,
    loss: '47% leave before page loads (Google data)'
  }, {
    label: 'Engaged Visitors',
    value: '320',
    icon: MousePointerClick,
    loss: '40% bounce without interacting'
  }, {
    label: 'Leads (form fills / calls)',
    value: '16',
    icon: CalendarX,
    loss: '5% lead conversion rate'
  }, {
    label: 'Booked Appointments',
    value: '4',
    icon: DollarSign,
    loss: '25% of leads actually book'
  }];
  return <section id="technology" className="min-h-screen bg-dark-section py-24 px-4 flex flex-col justify-center" data-id="element-185" data-mp="1kg7pq0ocxcec">
      <div className="max-w-7xl mx-auto w-full" data-id="element-186" data-mp="1p87q1u8ca7bg">
        <div className="text-center mb-16" data-id="element-187" data-mp="rs24lcte5ldz">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-188" data-mp="rrahyrhruxzu">
            Why Your Website Is Slow
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-189" data-mp="3875nyj9qzd3f">
            Client-Side vs Server-Side Rendering
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16" ref={ref} data-id="element-190" data-mp="3r46w2cylltbl">
          {/* CSR Card */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} className="bg-dark-card rounded-2xl p-8 relative overflow-hidden border-l-4 border-red-500" data-id="element-191" data-mp="3n1a7m5mdn2zq">
            <div className="flex items-center mb-6" data-id="element-192" data-mp="18uu4lbby8nyu">
              <div className="p-3 bg-red-500/10 rounded-lg mr-4" data-id="element-193" data-mp="1dugmvz6mwuei">
                <Monitor className="text-red-500" size={32} data-id="element-194" data-mp="1dslzme7w4q79" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-195" data-mp="yvwc5xbekaak">
                Client-Side Rendering (CSR)
              </h3>
            </div>
            <p className="text-text-muted mb-8 text-lg leading-relaxed" data-id="element-196" data-mp="pwrf6vfhop0i">
              ALL DIY website builders —{' '}
              <span className="text-white font-semibold" data-id="element-197" data-mp="uvcemsmx34nw">
                GoDaddy, Wix, GoHighLevel, Squarespace
              </span>{' '}
              — use client-side rendering because it's built to be cheap, not
              built to drive higher rankings with lightning-fast performance.
            </p>
            <div className="space-y-6" data-id="element-198" data-mp="2mvgs9oj5hh82">
              <div data-id="element-199" data-mp="2mt43osiw08m1">
                <div className="flex justify-between mb-2 text-sm font-medium" data-id="element-200" data-mp="2itmw9tjwh1e8">
                  <span className="text-red-400" data-id="element-201" data-mp="2ivpyrd28omzu">Load Time</span>
                  <span className="text-red-400" data-id="element-202" data-mp="2dvbn7l1fiux2">3-5 seconds</span>
                </div>
                <div className="h-4 bg-dark rounded-full overflow-hidden" data-id="element-203" data-mp="28w8pklns3qz4">
                  <motion.div className="h-full bg-red-500" initial={{
                  width: 0
                }} animate={isVisible ? {
                  width: '85%'
                } : {}} transition={{
                  duration: 2,
                  ease: 'easeOut'
                }} data-id="element-204" data-mp="36iy1bn8a9hy6" />
                </div>
              </div>
              <div className="flex items-center text-red-400" data-id="element-205" data-mp="3kgw4dwh5lryd">
                <XCircle size={20} className="mr-2" data-id="element-206" data-mp="3rvf3vckiqk71" />
                <span className="font-medium" data-id="element-207" data-mp="nu8n2bs0vec">
                  Search engines struggle to crawl
                </span>
              </div>
            </div>
          </motion.div>

          {/* SSR Card */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-dark-card rounded-2xl p-8 relative overflow-hidden border-l-4 border-primary" data-id="element-208" data-mp="alqhtol5lg9t">
            <div className="flex items-center mb-6" data-id="element-209" data-mp="ybuipo6yj1x4">
              <div className="p-3 bg-primary/10 rounded-lg mr-4" data-id="element-210" data-mp="yd5k5hcxjxl2">
                <Server className="text-primary" size={32} data-id="element-211" data-mp="12ceikzix0jmy" />
              </div>
              <h3 className="text-2xl font-bold text-white" data-id="element-212" data-mp="1nnutrh91b45b">
                Server-Side Rendering (SSR)
              </h3>
            </div>
            <p className="text-text-muted mb-8 text-lg leading-relaxed" data-id="element-213" data-mp="1jlrhwrigdxji">
              Pre-render on server. Fast. SEO-optimized. Google crawls complete
              HTML immediately.
            </p>
            <div className="space-y-6" data-id="element-214" data-mp="1snqxrghqevkv">
              <div data-id="element-215" data-mp="14xmuja8ggxue">
                <div className="flex justify-between mb-2 text-sm font-medium" data-id="element-216" data-mp="14x3oorw2xzwq">
                  <span className="text-primary" data-id="element-217" data-mp="zyk2qrhhko4d">Load Time</span>
                  <span className="text-primary" data-id="element-218" data-mp="100d905o6t6bu">&lt; 1 second</span>
                </div>
                <div className="h-4 bg-dark rounded-full overflow-hidden" data-id="element-219" data-mp="1ep0ypvbcqltv">
                  <motion.div className="h-full bg-primary" initial={{
                  width: 0
                }} animate={isVisible ? {
                  width: '20%'
                } : {}} transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                  delay: 0.5
                }} data-id="element-220" data-mp="19vopasf8ullp" />
                </div>
              </div>
              <div className="flex items-center text-green-500" data-id="element-221" data-mp="2nxcn97vmzxbl">
                <CheckCircle size={20} className="mr-2" data-id="element-222" data-mp="3bm5bsj9xj2w6" />
                <span className="font-medium" data-id="element-223" data-mp="3bmotpzzkxra7">
                  Perfect crawlability & Indexing
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Live Performance Comparison */}
        <div className="bg-dark-card rounded-2xl p-8 border border-white/5" data-id="element-224" data-mp="37kaedpv4d9no">
          <h4 className="text-xl font-bold mb-6 text-center" data-id="element-225" data-mp="37memkk01wutr">
            Live Performance Comparison
          </h4>

          <div className="space-y-6" data-id="element-226" data-mp="1w557sarvaqys">
            {/* SSR Race Bar */}
            <div className="relative" data-id="element-227" data-mp="1w3278wt39fft">
              <div className="flex justify-between text-sm mb-2" data-id="element-228" data-mp="1whhsv8f0fgb7">
                <span className="font-bold text-white" data-id="element-229" data-mp="1wk48zwfyv451">LetLois (SSR)</span>
                <span className="text-primary font-mono font-bold" data-id="element-230" data-mp="20j3ebi5m12x0">
                  Loaded: {ssrLoads}x
                </span>
              </div>
              <div className="h-8 bg-dark rounded-full overflow-hidden relative border border-white/5" data-id="element-231" data-mp="1rr1gzsqu82qy">
                <div className="h-full bg-primary absolute left-0 top-0" style={{
                width: `${ssrWidth}%`,
                transition: 'none'
              }} data-id="element-232" data-mp="1ctit0slzhtkk" />
                {ssrDone && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-xs font-bold flex items-center" data-id="element-233" data-mp="13voxhjw1n3yg">
                    <CheckCircle size={12} className="mr-1" data-id="element-234" data-mp="2y6ty0fbu309k" /> LOADED
                  </div>}
              </div>
            </div>

            {/* CSR Race Bar */}
            <div className="relative" data-id="element-235" data-mp="3daekpsorm30j">
              <div className="flex justify-between text-sm mb-2" data-id="element-236" data-mp="3dbg8mxqvqjzo">
                <span className="font-bold text-white" data-id="element-237" data-mp="38ba9yxm6zx83">Competitors (CSR)</span>
                <span className="text-red-400 font-mono font-bold" data-id="element-238" data-mp="3897g22hx5jy3">
                  Loaded: 1x
                </span>
              </div>
              <div className="h-8 bg-dark rounded-full overflow-hidden relative border border-white/5" data-id="element-239" data-mp="3hbgpsq5d59af">
                <div className="h-full bg-red-500 absolute left-0 top-0" style={{
                width: `${csrWidth}%`,
                transition: 'none'
              }} data-id="element-240" data-mp="3w03bd9ll2xa3" />
                {csrDone && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-xs font-bold flex items-center" data-id="element-241" data-mp="4uld63cew1vm">
                    <CheckCircle size={12} className="mr-1" data-id="element-242" data-mp="2amrx0w9uge8f" /> LOADED
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-text-muted" data-id="element-243" data-mp="26oaz93cbcg3o">
            SSR loads <span className="text-primary font-bold" data-id="element-244" data-mp="9n56oxe34ttz">3x faster</span>{' '}
            than CSR on average.
          </div>
        </div>

        {/* Slow Website Funnel Drop-off */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="mt-16 bg-dark-card rounded-2xl p-8 border border-red-500/20" data-id="element-245" data-mp="jhx0rr8gkyk0">
          <h4 className="text-2xl font-bold mb-2 text-center" data-id="element-246" data-mp="12baopcdr84q4">
            What a Slow Website <span className="text-red-500" data-id="element-247" data-mp="179c85yf3e624">Actually</span>{' '}
            Costs You
          </h4>
          <p className="text-text-muted text-center mb-10 text-sm" data-id="element-248" data-mp="1c8ymycakjud1">
            Per 1,000 visitors with a 3-5 second load time — based on Google &
            Unbounce data
          </p>

          <div className="max-w-2xl mx-auto space-y-0" data-id="element-249" data-mp="30sb5qmtw7yh8">
            {funnelSteps.map((step, i) => {
            const widthPercent = 100 - i * 16;
            const isLast = i === funnelSteps.length - 1;
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.5 + i * 0.15
            }} data-id="element-250" data-mp="2rrn28m24lgil" data-mpi={i}>
                  {step.loss && <div className="flex items-center justify-center mb-1" data-id="element-251" data-mp="35qme0t4ji0xc">
                      <div className="text-red-400 text-xs font-bold flex items-center bg-red-500/10 px-3 py-1 rounded-full border border-red-500/20" data-id="element-252" data-mp="71xl22dy9imq">
                        <XCircle size={12} className="mr-1" data-id="element-253" data-mp="3ixjxmujsaxn4" />
                        {step.loss}
                      </div>
                    </div>}
                  <div className="mx-auto flex items-center justify-between px-4 py-3 rounded-lg mb-1" style={{
                width: `${widthPercent}%`,
                background: i === 0 ? 'rgba(255,255,255,0.08)' : `rgba(239, 68, 68, ${0.08 + i * 0.07})`,
                borderLeft: i > 0 ? '3px solid rgba(239, 68, 68, 0.5)' : '3px solid rgba(255,255,255,0.2)',
                borderRight: i > 0 ? '3px solid rgba(239, 68, 68, 0.5)' : '3px solid rgba(255,255,255,0.2)'
              }} data-id="element-254" data-mp="3n0glp0s3flyg">
                    <span className={`text-sm font-medium ${i === 0 ? 'text-white' : 'text-red-300'}`} data-id="element-255" data-mp="tab81rlldh78">
                      {step.label}
                    </span>
                    <span className={`text-lg font-bold ${i === 0 ? 'text-white' : isLast ? 'text-red-500' : 'text-red-400'}`} data-id="element-256" data-mp="28xpuw7uthxq0">
                      {step.value}
                    </span>
                  </div>
                </motion.div>;
          })}
          </div>

          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 1.5
        }} className="mt-8 text-center" data-id="element-257" data-mp="1q17uz35terio">
            <p className="text-red-400 font-bold text-lg" data-id="element-258" data-mp="3fe1n7saykldo">
              With a fast SSR site, that same 1,000 visitors could yield{' '}
              <span className="text-green-400" data-id="element-259" data-mp="2833wc56b829a">12+ booked appointments</span> —
              3x more.
            </p>
            <p className="text-text-dim text-xs mt-2" data-id="element-260" data-mp="2d1owy5ph4ny9">
              Source: Google "Think with Google" mobile speed study, Unbounce
              conversion benchmark report
            </p>
          </motion.div>
        </motion.div>

        {/* Callout */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.8
      }} className="mt-12 flex items-center justify-center p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-500" data-id="element-261" data-mp="2m0sh7d0dn9ec">
          <AlertTriangle className="mr-3" data-id="element-262" data-mp="15p6bk8oe0rxl" />
          <span className="font-bold" data-id="element-263" data-mp="15od578tcc4fz">
            Fact: Every second slower = 7% fewer conversions
          </span>
        </motion.div>
      </div>
    </section>;
}